import React, { useState } from 'react';
import { Home, Info, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface TechnicianLegendItem {
  id: string;
  name: string;
  color: string;
}

interface MapLegendProps {
  colorByTechnician?: boolean;
  technicians?: TechnicianLegendItem[];
}

// Persist the collapsed/expanded choice and whether the first-time hint was seen.
const OPEN_KEY = 'vehix_map_legend_open';
const HINT_KEY = 'vehix_map_legend_hint_seen';

const readBool = (key: string): boolean | null => {
  try {
    const v = localStorage.getItem(key);
    return v === null ? null : v === 'true';
  } catch {
    return null;
  }
};
const writeBool = (key: string, value: boolean): void => {
  try {
    localStorage.setItem(key, String(value));
  } catch {
    // best-effort; ignore storage failures
  }
};

/** Small swatch that mimics a dealer bubble. */
const BubbleSwatch: React.FC<{
  ring?: string;
  fill?: string;
  desaturated?: boolean;
  check?: boolean;
}> = ({ ring, fill = '#7C5F2C', desaturated, check }) => (
  <span className='relative inline-flex h-5 w-5 shrink-0 items-center justify-center'>
    {ring && (
      <span
        className='absolute inset-0 rounded-full'
        style={{ background: ring, padding: '2px' }}
      />
    )}
    <span
      className='relative h-3.5 w-3.5 rounded-full border border-white'
      style={{ backgroundColor: fill, filter: desaturated ? 'grayscale(0.7)' : undefined }}
    />
    {check && (
      <span
        className={cn(
          'absolute -bottom-0.5 -right-0.5 flex h-2.5 w-2.5 items-center justify-center',
          'rounded-full border border-white bg-emerald-500 text-[7px] font-bold text-white'
        )}
        aria-hidden
      >
        ✓
      </span>
    )}
  </span>
);

const CUSTOMER_SWATCH_CLASS =
  'flex h-3.5 w-3.5 items-center justify-center rounded-[3px] border border-white bg-[#0ea5e9]';

/** Small swatch that mimics a customer-stop marker (rounded square + house). */
const CustomerSwatch: React.FC = () => (
  <span className='inline-flex h-5 w-5 shrink-0 items-center justify-center'>
    <span className={CUSTOMER_SWATCH_CLASS}>
      <Home className='h-2.5 w-2.5 text-white' strokeWidth={3} aria-hidden />
    </span>
  </span>
);

const LegendRows: React.FC<MapLegendProps> = ({ colorByTechnician, technicians }) => (
  <div className='space-y-1.5 text-xs text-foreground'>
    <div className='flex items-center gap-2'>
      <BubbleSwatch />
      <span>Bubble size = number of recalls</span>
    </div>
    <div className='flex items-center gap-2'>
      <BubbleSwatch ring='conic-gradient(#10b981 0 50%, rgba(124,95,44,0.25) 50% 100%)' />
      <span>Green ring = work assigned</span>
    </div>
    <div className='flex items-center gap-2'>
      <BubbleSwatch desaturated check />
      <span>Faded + ✓ = all assigned</span>
    </div>
    <div className='flex items-center gap-2'>
      <BubbleSwatch fill='#0ea5e9' />
      <span>Blue circle = manual stop (business)</span>
    </div>
    <div className='flex items-center gap-2'>
      <CustomerSwatch />
      <span>Blue square = manual stop (customer)</span>
    </div>
    {colorByTechnician && technicians && technicians.length > 0 && (
      <div className='mt-1 border-t border-border pt-1.5'>
        <p className='mb-1 font-medium text-muted-foreground'>Technicians</p>
        <div className='max-h-32 space-y-1 overflow-y-auto pr-1'>
          {technicians.map(tech => (
            <div key={tech.id} className='flex items-center gap-2'>
              <span
                className='h-2.5 w-2.5 shrink-0 rounded-full'
                style={{ backgroundColor: tech.color }}
              />
              <span className='truncate'>{tech.name}</span>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
);

/**
 * Map legend overlay explaining the dealer-marker encoding.
 *
 * Learnability vs. efficiency: opens by default on large screens (collapsed on
 * small ones), and once the user collapses it that choice is remembered so power
 * users keep a clean map. Collapsed, it stays an obvious, keyboard-accessible
 * "Legend" pill in the same corner. The marker tooltips provide just-in-time
 * detail so the legend is support, not the only source of truth.
 */
/* eslint-disable max-lines-per-function */
export const MapLegend: React.FC<MapLegendProps> = ({
  colorByTechnician = false,
  technicians = []
}) => {
  const [open, setOpen] = useState<boolean>(() => {
    const saved = readBool(OPEN_KEY);
    if (saved !== null) return saved;
    // No stored preference: open on large screens, collapsed on small/dense ones.
    return typeof window !== 'undefined' ? window.innerWidth >= 1024 : true;
  });
  const [hintSeen, setHintSeen] = useState<boolean>(() => readBool(HINT_KEY) === true);

  const markHintSeen = () => {
    if (!hintSeen) {
      setHintSeen(true);
      writeBool(HINT_KEY, true);
    }
  };

  const collapse = () => {
    setOpen(false);
    writeBool(OPEN_KEY, false);
    markHintSeen(); // collapsing implies they've learned the icons
  };

  const expand = () => {
    setOpen(true);
    writeBool(OPEN_KEY, true);
  };

  return (
    <div className='absolute top-4 right-4 z-[1000]'>
      {open ? (
        <div className='w-56 rounded-lg border bg-card/95 p-3 shadow-md backdrop-blur-sm'>
          <div className='mb-1.5 flex items-center justify-between gap-4'>
            <span className='text-xs font-medium text-foreground'>Map legend</span>
            <button
              type='button'
              aria-label='Collapse map legend'
              aria-expanded={true}
              onClick={collapse}
              className='text-muted-foreground hover:text-foreground'
            >
              <X className='h-3.5 w-3.5' />
            </button>
          </div>
          <LegendRows colorByTechnician={colorByTechnician} technicians={technicians} />
          {!hintSeen && (
            <div
              className={cn(
                'mt-2 flex items-start gap-2 border-t border-border pt-2',
                'text-[11px] text-muted-foreground'
              )}
            >
              <span>You can collapse this legend once you’re familiar with the icons.</span>
              <button
                type='button'
                onClick={markHintSeen}
                className={cn(
                  'shrink-0 font-medium hover:underline',
                  'text-brand-primary-600 dark:text-brand-primary-400'
                )}
              >
                Got it
              </button>
            </div>
          )}
        </div>
      ) : (
        <button
          type='button'
          onClick={expand}
          aria-label='Map legend, collapsed. Press Enter to expand.'
          aria-expanded={false}
          className={cn(
            'flex items-center gap-1.5 rounded-full border bg-card/95 px-3 py-1.5',
            'text-xs font-medium text-foreground shadow-md backdrop-blur-sm',
            'hover:bg-brand-primary-50 dark:hover:bg-brand-primary-500/10'
          )}
        >
          <Info className='h-3.5 w-3.5 text-brand-primary-600 dark:text-brand-primary-400' />
          Legend
        </button>
      )}
    </div>
  );
};

export default MapLegend;
